<template>
	<modal
		class="billing-payment-modal"
		size="medium"
		@close="$emit('close')"
		v-if="isVisible && (billing || isBulk)">
		<template #header>
			<h3>{{ $t('Billing Payment') }}</h3>
		</template>
		<template #body>
			<div class="callout callout--info mb-3 bg-blue-light">
				<template v-if="isBulk">
					<div class="text-bold text-dark mb-1">{{ $t('Bulk Payment') }}</div>
					<div class="text-muted text-sm">{{ billings.length }} {{ $t('Billings Selected') }}</div>
				</template>
				<template v-else>
					<div class="text-bold text-dark mb-1">{{ billing.description }}</div>
					<div class="text-muted text-sm">ID: #{{ billing.billing_id }}</div>
				</template>
			</div>

			<!-- Payment Method Selection -->
			<div class="form-group my-4">
				<label class="text-bold mb-3">{{ $t('Payment Method') }}</label>
				<div class="payment-methods">
					<div
						v-for="method in paymentMethods"
						:key="method.code"
						class="payment-method-card"
						:class="{ active: selectedPaymentMethod.code === method.code }"
						@click="selectedPaymentMethod = method">
						<div class="payment-method-icon">
							<template v-if="method.iconType === 'image'">
								<img :src="method.iconUrl" :alt="method.name" width="30" />
							</template>
							<i v-else :class="method.icon"></i>
						</div>
						<div class="payment-method-info">
							<small class="payment-method-name">{{ method.name }}</small>
						</div>
						<div class="payment-method-radio">
							<input
								type="radio"
								:value="method"
								v-model="selectedPaymentMethod"
								:id="`payment-${method.code}`">
						</div>
					</div>
				</div>
			</div>

			<!-- Fee Breakdown -->
			<div class="card bg-light">
				<div class="card-body">
					<h6 class="card-title text-bold mb-3">{{ $t('Rincian Pembayaran') }}</h6>

					<!-- Subtotal/Nominal -->
					<div class="d-flex justify-content-between align-items-center mb-2">
						<span>{{ isSubscription ? $t('Subscription Fee') : $t('Billing Value') }}</span>
						<span class="text-bold">Rp {{ nominal.toLocaleString('id-ID') }}</span>
					</div>

					<!-- PPN (Only for Subscription) -->
					<div v-if="isSubscription" class="d-flex justify-content-between align-items-center mb-2">
						<span>PPN (11%)</span>
						<span class="text-bold">Rp {{ ppn.toLocaleString('id-ID') }}</span>
					</div>

					<!-- Administrative Fee -->
					<div class="d-flex justify-content-between align-items-center mb-3">
						<span>
							{{ $t('Admin Fee') }}
							<small v-if="selectedPaymentMethod.code === 'other_qris'" class="text-muted">
								{{ `(${selectedPaymentMethod.fee_value}% dari nilai tagihan)` }}
							</small>
						</span>
						<span class="text-bold">Rp {{ adminFee.toLocaleString('id-ID') }}</span>
					</div>

					<!-- Total -->
					<div class="border-top pt-3">
						<div class="d-flex justify-content-between align-items-center">
							<span class="text-bold" style="font-size: 1.1em;">{{ $t('Total Payment') }}</span>
							<span class="text-success" style="font-size: 1.3em; font-weight: bold;">
								Rp {{ totalPayment.toLocaleString('id-ID') }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<!-- Cancel -->
			<button
				type="button"
				class="btn btn-light"
				@click="$emit('close')">
				{{ $t('Cancel') }}
			</button>
			<!-- Submit -->
			<button
				class="btn btn-primary"
				:class="{ 'is-loading': isProcessing }"
				:disabled="isProcessing"
				@click="processPayment">
				<span>{{ $t('Proceed to Payment') }}</span>
			</button>
		</template>
	</modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import { getAxiosErrorMessage } from '@/lib/helper';
import billingsApi from '@/api/billings';

const paymentMethods = [
	{
		code: 'bca_va',
		name: 'BCA VA',
		description: 'Pay using BCA Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bca.png',
		fee_type: 'fixed',
		fee_value: 5000,
	},
	{
		code: 'bni_va',
		name: 'BNI VA',
		description: 'Pay using BNI Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bni.png',
		fee_type: 'fixed',
		fee_value: 5000,
	},
	{
		code: 'bri_va',
		name: 'BRI VA',
		description: 'Pay using BRI Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bri.png',
		fee_type: 'fixed',
		fee_value: 5000,
	},
	{
		code: 'echannel',
		name: 'Mandiri Bill',
		description: 'Pay using Mandiri Bill',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-mandiri.png',
		fee_type: 'fixed',
		fee_value: 5000,
	},
	{
		code: 'permata_va',
		name: 'Permata VA',
		description: 'Pay using Permata Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-permata.png',
		fee_type: 'fixed',
		fee_value: 5000,
	},
	{
		code: 'other_qris',
		name: 'QRIS',
		description: 'Pay using QRIS',
		icon: 'fas fa-qrcode',
		iconType: 'font',
		fee_type: 'percentage',
		fee_value: 0.7,
	},
];

export default {
	name: 'BillingPaymentModal',
	components: {
		Modal,
	},
	props: {
		isVisible: {
			type: Boolean,
			default: false,
		},
		billing: {
			type: Object,
			default: null,
		},
		isBulk: {
			type: Boolean,
			default: false,
		},
		billings: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isProcessing: false,
			paymentMethods,
			selectedPaymentMethod: paymentMethods[0],
		};
	},
	computed: {
		isSubscription() {
			if (this.isBulk) return false;
			return this.billing?.type === 'subscription';
		},
		nominal() {
			if (this.isBulk) {
				return this.billings.reduce((sum, item) => sum + (item.value || 0), 0);
			}
			if (!this.billing) return 0;
			return this.isSubscription ? Math.round(this.billing.value / 1.11) : this.billing.value;
		},
		ppn() {
			if (this.isBulk) return 0;
			if (!this.billing) return 0;
			return this.isSubscription ? this.billing.value - this.nominal : 0;
		},
		billingValue() {
			if (this.isBulk) {
				return this.billings.reduce((sum, item) => sum + (item.value || 0), 0);
			}
			return this.billing ? this.billing.value : 0;
		},
		adminFee() {
			const value = this.billingValue;
			if (value === 0 || !this.selectedPaymentMethod) return 0;
			if (this.selectedPaymentMethod.fee_type === 'fixed') {
				return this.selectedPaymentMethod.fee_value;
			}
			if (this.selectedPaymentMethod.fee_type === 'percentage') {
				return Math.ceil((value * this.selectedPaymentMethod.fee_value) / 100);
			}
			return 0;
		},
		totalPayment() {
			return this.billingValue + this.adminFee;
		},
	},
	methods: {
		processPayment() {
			if (!this.isBulk && !this.billing) return;
			if (this.isBulk && (!this.billings || this.billings.length === 0)) return;

			this.isProcessing = true;
			const selectedPaymentMethod = this?.selectedPaymentMethod?.code || this.paymentMethods[0].code;
			const params = {
				provider: 'midtrans',
				payment_method: selectedPaymentMethod,
			};

			if (this.isBulk) {
				const billingIds = this.billings.map(item => item._id);
				params.billing_ids = JSON.stringify(billingIds);

				const callback = (response) => {
					this.isProcessing = false;
					this.$emit('close');
					const data = response.data;
					if (data && data.token) {
						this.$emit('success-group', { token: data.token, id: data._id });
					}
				};

				const errorCallback = (e) => {
					this.isProcessing = false;
					const message = getAxiosErrorMessage(e);
					this.$notify({
						group: 'app',
						type: 'error',
						title: this.$t('Payment Failed'),
						text: message,
					});
				};

				billingsApi.getPaymentTokenGroup(params, callback, errorCallback);
			} else {
				const callback = (response) => {
					this.isProcessing = false;
					this.$emit('close');
					const token = response.data;
					if (token) {
						this.$emit('success', token);
					}
				};

				const errorCallback = (e) => {
					this.isProcessing = false;
					const message = getAxiosErrorMessage(e);
					this.$notify({
						group: 'app',
						type: 'error',
						title: this.$t('Payment Failed'),
						text: message,
					});
				};

				billingsApi.getPaymentToken(this.billing._id, params, callback, errorCallback);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.payment-methods {
	display: flex;
	gap: 7px;
	flex-wrap: wrap;
}

.payment-method-card {
	display: flex;
	align-items: center;
	padding: 5px 8px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: #fff;
}

.payment-method-card:hover {
	border-color: #006DC7;
	background-color: #dceefd;
}

.payment-method-card.active {
	border-color: #006DC7;
	background-color: #dceefd;
	box-shadow: 0 2px 8px rgba(0, 109, 199, 0.15);
}

.payment-method-icon {
	font-size: 18px;
	margin-right: 8px;
	text-align: center;
	color: #006DC7;
	display: flex;
	align-items: center;
}

.payment-method-info {
	flex: 1;
	margin-right: 10px;
}

.payment-method-name {
	margin: 0;
	font-weight: 600;
	font-size: 12px;
	color: #333;
}

.payment-method-radio {
	flex-shrink: 0;
	display: none;
}
</style>
